import React, { useMemo } from "react";
import { Button, Col, DatePicker, Form, Input, Select } from "antd";
import styled from "styled-components";
import PopupBackground from "@component/PopupBackground";
import cityRegionData, { CityExcludedArea } from "@utils/CityRegionData";
import moment from "moment";
import { fetchCreateOrder, fetchUpdateOrder } from "@redux/onlineOrderSlice";
import { useDispatch } from "react-redux";
import { CreateOrder, OrderDetail, UpdateOrder } from "@api/throughShipment/onlineOrderApi";
import { useParams } from "react-router-dom";
import useCityRegionCode from "@hooks/useCityRegionCode";
import { RetailList, InvoiceMode } from "@api/throughShipment/throughShipmentApi";
import { tcatRetailersOptions, getWarehouseCode } from "@constant/WarehouseCodeValue";
import CarrierTypeValue, { URMART_CARRIER_NUMBER } from "@page/Order/CarrierTypeValue";

const FormWrapper = styled(Form)`
  width: 500px;
  padding: 20px;
  background-color: ${({ theme }) => theme.colorNeutral100};
  display: flex;
  flex-direction: column;
  .ant-form-item-label > label.ant-form-item-required:not(.ant-form-item-required-mark-optional)::before {
    order: 1;
  }
  .ant-form-item-label > label.ant-form-item-required:not(.ant-form-item-required-mark-optional)::after {
    margin: 0;
  }
  .ant-form-item {
    margin-bottom: 5px;
  }
`;
const Reminder = styled.p`
  color: #ec6c27;
`;
const CityWrapper = styled.div`
  display: flex;
  margin-bottom: 10px;
  justify-content: space-between;
`;
const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: auto;
`;
const RequiredIcon = styled.span`
  color: red;
`;
const Row = styled.div`
  display: flex;
`;
const AddWrapper = styled.div`
  width: 100%;
`;
type Props = {
  onClose: () => void;
  onlineOrderDetail?: OrderDetail;
  retailerList?: RetailList[];
};
type InvoiceResetFields = Partial<{
  invoiceNumber: undefined;
  invoiceAt: undefined;
  email: undefined;
  carrierTypeValue: undefined;
  carrierNumber: undefined;
}>;
const EditPopup = (props: Props) => {
  const { onClose, onlineOrderDetail, retailerList } = props;
  const dispatch = useDispatch();
  const { orderId } = useParams();
  const [form] = Form.useForm();
  const { getPostCodeWithCVS } = useCityRegionCode();

  const allCities = Object.keys(cityRegionData);

  const platform = Form.useWatch("platform", form);
  const isB2C = platform?.startsWith("B2C");

  const currentCity = Form.useWatch("city", form);
  const selectedWarehouse = Form.useWatch("warehouse", form);
  const selectedCarrierTypeValue = Form.useWatch("carrierTypeValue", form);

  const invoiceMode = onlineOrderDetail?.invoiceMode ?? retailerList?.find((r) => r.name === platform)?.invoiceMode;

  const getWarehouseInitValue = (warehouseCode?: string) => {
    if (!warehouseCode) return "藍田";
    return getWarehouseCode(warehouseCode)?.label;
  };

  const handleSelectCity = () => {
    form.setFieldsValue({ region: undefined, address: undefined });
  };

  const renderRegionOptions = (city: string) => {
    if (!city || !cityRegionData[city]) {
      return [];
    }
    const allRegions = Object.keys(cityRegionData[city]);
    return allRegions.map((region) => (
      <Select.Option value={region} key={region}>
        {region}
      </Select.Option>
    ));
  };

  const handleOnSubmit = (formValue: any) => {
    const baseParams = {
      warehouse: formValue.warehouse,
      platform: formValue.platform,
      grandTotal: isB2C ? formValue.grandTotal : 0,
      estimatedShippingDate: formValue.estimatedShippingDate
        ? moment(formValue.estimatedShippingDate).format("YYYY-MM-DD")
        : "",
      estimatedArrivalDate: formValue.estimatedArrivalDate
        ? moment(formValue.estimatedArrivalDate).format("YYYY-MM-DD")
        : "",
      platformNumber: formValue.platformNumber,
      shippingMethod: formValue.shippingMethod,
      receiverName: formValue.receiverName,
      receiverPhone: formValue.receiverPhone,
      receiverZipcode: cityRegionData[formValue.city][formValue.region],
      receiverAddress: `${formValue.city}${formValue.region}${formValue.address}`,
      note: formValue.note,
    };

    let params: CreateOrder;
    if (invoiceMode === InvoiceMode.AUTO_INVOICE) {
      params = {
        ...baseParams,
        receiverEmail: formValue.email,
        carrierType: formValue.carrierTypeValue,
        carrierNumber: formValue.carrierNumber,
      };
    } else if (invoiceMode === InvoiceMode.THIRD_PARTY_FILL) {
      params = {
        ...baseParams,
        invoiceNumber: formValue.invoiceNumber,
        invoiceAt: formValue.invoiceAt ? moment(formValue.invoiceAt).format("YYYY-MM-DD") : "",
      };
    } else {
      params = baseParams;
    }
    if (onlineOrderDetail && orderId) {
      // 編輯
      const updateParams: UpdateOrder = { ...params, orderId };
      dispatch(fetchUpdateOrder(updateParams));
    } else {
      // 新增
      dispatch(fetchCreateOrder(params));
    }

    onClose();
  };

  const selectedPlatform: {
    [key: string]: {
      receiverName: string;
      receiverPhone: string;
      city: string;
      region: string;
      address: string;
    };
  } = {
    "B2B-PChome": {
      receiverName: "15庫-進貨組",
      receiverPhone: "02-27000898#3905",
      city: "桃園市",
      region: "龜山區",
      address: "文信路288號",
    },
    "B2B-博客來": {
      receiverName: "周姚伶",
      receiverPhone: "02-27821100 #844",
      city: "新北市",
      region: "樹林區",
      address: "佳園路二段70-1號(大智通物流中心)",
    },
    "B2B-Yahoo": {
      receiverName: "雅虎進倉區",
      receiverPhone: "03-3891316 #8121",
      city: "桃園市",
      region: "大溪區",
      address: "仁和路二段350號",
    },
    "B2B-MOMO": {
      receiverName: "MOMO進貨組",
      receiverPhone: "02-6600-7600",
      city: "台北市",
      region: "北投區",
      address: "承德路七段400號",
    },
    "B2B-蝦皮寄倉": {
      receiverName: "樂購商城-進貨組",
      receiverPhone: "03-262-6560 #3151",
      city: "桃園市",
      region: "楊梅區",
      address: "和平路576號 (物流共和國-楊梅-2B)",
    },
  };

  const handleOnPlatformChange = (value: string) => {
    const selectedRetailer = retailerList?.find((r) => r.name === value);
    const newInvoiceMode = selectedRetailer?.invoiceMode;
    form.setFieldsValue({ platformRegionName: selectedRetailer?.region?.name });

    if (value.startsWith("B2B")) {
      const selectedValue = selectedPlatform[value];
      if (selectedValue) {
        form.setFieldsValue({
          receiverName: selectedValue.receiverName,
          receiverPhone: selectedValue.receiverPhone,
          city: selectedValue.city,
          region: selectedValue.region,
          address: selectedValue.address,
        });
      }
    } else {
      form.setFieldsValue({
        receiverName: undefined,
        receiverPhone: undefined,
        city: undefined,
        region: undefined,
        address: undefined,
        email: undefined,
        carrierTypeValue: undefined,
        carrierNumber: undefined,
        invoiceNumber: undefined,
        invoiceAt: undefined,
      });
    }

    const resetFields: Record<InvoiceMode, InvoiceResetFields> = {
      [InvoiceMode.AUTO_INVOICE]: { invoiceNumber: undefined, invoiceAt: undefined },
      [InvoiceMode.THIRD_PARTY_FILL]: { email: undefined, carrierTypeValue: undefined, carrierNumber: undefined },
      [InvoiceMode.MONTHLY_STATEMENT]: {
        email: undefined,
        carrierTypeValue: undefined,
        carrierNumber: undefined,
        invoiceNumber: undefined,
        invoiceAt: undefined,
      },
    };

    if (newInvoiceMode != null) {
      const fields = resetFields[newInvoiceMode];
      if (fields) form.setFieldsValue(fields);
    }
  };

  const handleWarehouseChange = () => {
    form.setFieldsValue({ shippingMethod: undefined });
  };

  const platformOptions = useMemo(() => {
    return (
      retailerList
        ?.filter((retailer) => {
          if (selectedWarehouse === "黑貓") {
            return tcatRetailersOptions.includes(retailer.name);
          }
          return true;
        })
        .map((retailer) => ({ label: retailer.name, value: retailer.name })) || []
    );
  }, [retailerList, selectedWarehouse]);

  const validateCarrierNumber = ({ getFieldValue }: { getFieldValue: (name: string) => CarrierTypeValue | null }) => ({
    validator(_: unknown, value: string) {
      const carrierTypeValue = getFieldValue("carrierTypeValue") as CarrierTypeValue;
      const trimmedValue = value?.trim() || "";

      // 「不需驗證」的情況：未選擇 或 優馬載具
      // 因 URMART=0，所以 !carrierTypeValue 可同時涵蓋兩種狀況
      if (!carrierTypeValue) {
        return Promise.resolve();
      }

      // 「未填寫」的情況
      if (!trimmedValue) {
        // 若結帳模式為系統自動開發票，空值為非法
        if (invoiceMode === InvoiceMode.AUTO_INVOICE) {
          return Promise.reject(new Error("此欄位為必填"));
        }
        // 其他通路空值合法
        return Promise.resolve();
      }

      // 「已填寫」的情況：手機載具 和 自然人憑證
      const CARRIER_VALIDATION_RULES: Partial<Record<CarrierTypeValue, { pattern: RegExp; message: string }>> = {
        [CarrierTypeValue.PHONECODE]: {
          pattern: /^\/[A-Za-z0-9.+-]{7}$/,
          message: "手機條碼格式錯誤：第1碼為「/」，後7碼為英文、數字或「.」、「-」、「+」符號",
        },
        [CarrierTypeValue.NATURALCODE]: {
          pattern: /^[A-Z]{2}[0-9]{14}$/,
          message: "自然人憑證條碼格式錯誤：2碼大寫英字+14碼數字",
        },
      };

      const rule = CARRIER_VALIDATION_RULES[carrierTypeValue];
      // 如果該類型有定義規則，且格式不符，則報錯
      if (rule && !rule.pattern.test(trimmedValue)) {
        return Promise.reject(new Error(rule.message));
      }

      return Promise.resolve();
    },
  });

  const warehouseOptions = [
    { label: "藍田", value: "藍田" },
    { label: "黑貓", value: "黑貓" },
  ];

  const renderCarrierInput = () => {
    // 會員載具統一處理
    if (selectedCarrierTypeValue === CarrierTypeValue.URMART) {
      return <p>選擇會員載具，中獎發票將會以E-mail通知客戶</p>;
    }

    if (onlineOrderDetail) {
      return <Input />;
    }

    switch (selectedCarrierTypeValue) {
      case CarrierTypeValue.PHONECODE:
        return <Input placeholder="請輸入手機條碼" />;

      case CarrierTypeValue.NATURALCODE:
        return <Input placeholder="請輸入自然人憑證條碼" />;

      default:
        return <Input placeholder="請先選擇發票類型" disabled />;
    }
  };

  const INVOICE_REQUIRED_MESSAGE = "此欄位為必填";

  const renderInvoiceModeFields = () => {
    if (invoiceMode === InvoiceMode.AUTO_INVOICE) {
      return (
        <>
          <Form.Item
            label="收件人Email"
            name="email"
            rules={[
              {
                pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                message: "請輸入正確的Email格式",
              },
              { required: true, message: INVOICE_REQUIRED_MESSAGE },
            ]}
          >
            <Input minLength={5} maxLength={50} placeholder="此通路為必填" />
          </Form.Item>
          <Form.Item
            label="發票類型"
            name="carrierTypeValue"
            rules={[{ required: true, message: INVOICE_REQUIRED_MESSAGE }]}
          >
            <Select
              placeholder="請選擇"
              options={[
                { label: "手機載具", value: CarrierTypeValue.PHONECODE },
                { label: "自然人憑證", value: CarrierTypeValue.NATURALCODE },
                { label: "會員載具", value: CarrierTypeValue.URMART },
              ]}
              onChange={(value) => {
                if (value === CarrierTypeValue.URMART) {
                  form.setFieldsValue({ carrierNumber: URMART_CARRIER_NUMBER });
                } else {
                  form.setFieldsValue({ carrierNumber: "" });
                }
              }}
            />
          </Form.Item>
          <Form.Item label="載具/自然人憑證" name="carrierNumber" rules={[validateCarrierNumber]}>
            {renderCarrierInput()}
          </Form.Item>
        </>
      );
    }
    if (invoiceMode === InvoiceMode.THIRD_PARTY_FILL) {
      return (
        <>
          <Form.Item
            label="平台發票號碼"
            name="invoiceNumber"
            rules={[{ required: true, message: INVOICE_REQUIRED_MESSAGE }]}
          >
            <Input placeholder="此通路為必填" />
          </Form.Item>
          <Form.Item
            label="發票開立日期"
            name="invoiceAt"
            rules={[{ required: true, message: INVOICE_REQUIRED_MESSAGE }]}
          >
            <DatePicker placeholder="請選擇日期" style={{ width: "100%" }} />
          </Form.Item>
        </>
      );
    }
    return null;
  };

  return (
    <PopupBackground close={onClose}>
      <FormWrapper
        labelAlign="left"
        colon={false}
        onFinish={handleOnSubmit}
        form={form}
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 20 }}
        initialValues={{
          ...onlineOrderDetail,
          warehouse: getWarehouseInitValue(onlineOrderDetail?.warehouseCode),
          estimatedShippingDate: moment(onlineOrderDetail?.estimatedShippingDate),
          estimatedArrivalDate: moment(onlineOrderDetail?.estimatedArrivalDate),
          city: onlineOrderDetail && getPostCodeWithCVS(onlineOrderDetail.receiverAddress).city,
          region: onlineOrderDetail && getPostCodeWithCVS(onlineOrderDetail.receiverAddress).town,
          address: onlineOrderDetail && getPostCodeWithCVS(onlineOrderDetail.receiverAddress).address,
          email: onlineOrderDetail?.receiverEmail || "",
          carrierTypeValue: onlineOrderDetail?.carrierType ?? null,
          carrierNumber: onlineOrderDetail?.carrierNumber || "",
          platformRegionName: onlineOrderDetail?.regionName || "",
          invoiceNumber: onlineOrderDetail?.invoiceNumber || "",
          invoiceAt: onlineOrderDetail?.invoiceAt ? moment(onlineOrderDetail.invoiceAt) : undefined,
        }}
      >
        <Form.Item label="出庫倉庫" name="warehouse" rules={[{ required: true, message: "" }]}>
          <Select
            disabled={!!onlineOrderDetail}
            onChange={handleWarehouseChange}
            options={
              onlineOrderDetail
                ? [
                    {
                      label: getWarehouseCode(onlineOrderDetail.warehouseCode)?.label,
                      value: onlineOrderDetail.warehouseCode,
                    },
                  ]
                : warehouseOptions
            }
          />
        </Form.Item>
        <Form.Item shouldUpdate label="通路平台" name="platform" rules={[{ required: true, message: "" }]}>
          <Select
            disabled={!!onlineOrderDetail}
            placeholder="請選擇"
            onChange={handleOnPlatformChange}
            options={platformOptions}
          />
        </Form.Item>
        <Form.Item name="platformRegionName" noStyle>
          <Input type="hidden" />
        </Form.Item>
        <Form.Item shouldUpdate label="訂單總額" name="grandTotal" rules={[{ required: isB2C, message: "" }]}>
          {isB2C ? <Input /> : "-"}
        </Form.Item>
        <Form.Item
          label="預計出貨日"
          name="estimatedShippingDate"
          extra={<Reminder>*此資訊用於顯示倉庫備註中，僅供倉庫人員參考</Reminder>}
        >
          <DatePicker placeholder="請選擇日期" style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item
          label="預計到貨日"
          name="estimatedArrivalDate"
          extra={<Reminder>*此資訊用於顯示倉庫備註中，僅供倉庫人員參考</Reminder>}
        >
          <DatePicker placeholder="請選擇日期" style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item label="平台訂單編號" name="platformNumber" rules={[{ required: true, message: "" }]}>
          <Input maxLength={50} placeholder="最多50字元，只能輸入中,英,數,_" />
        </Form.Item>
        <Form.Item label="運送方式" name="shippingMethod" rules={[{ required: true, message: "" }]}>
          <Select placeholder="請選擇">
            {selectedWarehouse === "藍田" && (
              <>
                <Select.Option value="新竹物流">新竹物流</Select.Option>
                <Select.Option value="黑貓">黑貓</Select.Option>
                <Select.Option value="全盛貨運(邱哥)">全盛貨運(邱哥)</Select.Option>
                <Select.Option value="宅配通">宅配通</Select.Option>
              </>
            )}
            {selectedWarehouse === "黑貓" && <Select.Option value="黑貓宅配">黑貓宅配</Select.Option>}
          </Select>
        </Form.Item>
        <Form.Item label="收件人" name="receiverName" rules={[{ required: true, message: "" }]}>
          <Input />
        </Form.Item>
        <Form.Item label="收件電話" name="receiverPhone" rules={[{ required: true, message: "" }]}>
          <Input />
        </Form.Item>
        <Row>
          <Col span={6}>
            <span>收件地址</span>
            <RequiredIcon>*</RequiredIcon>
          </Col>
          <AddWrapper>
            <CityWrapper>
              <Form.Item
                wrapperCol={{ span: 24 }}
                name="city"
                rules={[{ required: true, message: "" }]}
                style={{ margin: "0px" }}
              >
                <Select placeholder="請選擇縣市" style={{ width: "150px" }} onChange={handleSelectCity}>
                  {allCities.map((cityName) => (
                    <Select.Option value={cityName} key={cityName}>
                      {cityName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                wrapperCol={{ span: 24 }}
                name="region"
                rules={[{ required: true, message: "" }]}
                style={{ margin: "0px" }}
              >
                <Select placeholder="請選擇區域" style={{ width: "150px" }}>
                  {renderRegionOptions(currentCity)}
                </Select>
              </Form.Item>
            </CityWrapper>
            <Form.Item
              wrapperCol={{ span: 24 }}
              name="address"
              rules={[
                {
                  required: true,
                  message: "",
                },
                {
                  validator: (_, value) => {
                    const region = form.getFieldValue("region");
                    const limitation = CityExcludedArea[region];
                    if (value && limitation && limitation.some((item) => value.includes(item))) {
                      return Promise.reject(new Error(`地址包含黑貓不寄送的地區：${limitation.join(", ")}`));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input />
            </Form.Item>
          </AddWrapper>
        </Row>
        {renderInvoiceModeFields()}
        <Form.Item label="備註" name="note">
          <Input placeholder="最少5字，最多50字" minLength={5} maxLength={50} />
        </Form.Item>
        <Footer>
          <Button
            style={{
              marginRight: "14px",
            }}
            onClick={() => onClose()}
          >
            取消
          </Button>
          <Button type="primary" htmlType="submit">
            儲存
          </Button>
        </Footer>
      </FormWrapper>
    </PopupBackground>
  );
};

export default EditPopup;
